// packages/web/src/components/Footer.tsx
import React, { useState } from 'react'
import { MapPin, Phone, Mail, Truck, Award, RefreshCw, Send, CheckCircle2 } from 'lucide-react'
import { useSiteConfig } from '../context/SiteConfigContext'
import { useFooterCareLinks, useCategoriesWithProducts } from '../lib/storeCategories'
import { companyLegalName, isGstEnabled } from '../lib/companyGst'
import { assetSrc } from '../lib/assets'

interface FooterProps {
  // intentionally empty; Footer navigates itself
}

export const Footer: React.FC<FooterProps> = () => {
  const { config } = useSiteConfig()
  const { categories: footerCategories } = useCategoriesWithProducts()
  const careLinks = useFooterCareLinks()
  const gstOn = isGstEnabled(config)
  const [newsletterEmail, setNewsletterEmail] = useState('')
  const [subscribed, setSubscribed] = useState(false)

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault()
    if (newsletterEmail) {
      setSubscribed(true)
      setTimeout(() => {
        setNewsletterEmail('')
        setSubscribed(false)
      }, 4000)
    }
  }

  const valueProps = [
    {
      icon: Award,
      title: config.prop1_title || 'Premium Gold Plated',
      subtext: config.prop1_subtext || 'Fashion Jewellery with Rich Gold Finish',
    },
    {
      icon: RefreshCw,
      title: config.prop2_title || '1-Year Color Warranty',
      subtext: config.prop2_subtext || 'Free Color Replacement',
    },
    {
      icon: Truck,
      title: config.prop3_title || 'Express COD Shipping',
      subtext: config.prop3_subtext || 'Insured Delivery Across India',
    },
    ...(gstOn
      ? [
          {
            icon: Award,
            title: config.prop4_title || 'GST Tax Invoicing',
            subtext: config.prop4_subtext || '3% CGST + 3% SGST Itemized',
          },
        ]
      : []),
  ]

  return (
    <footer className="bg-[var(--bg-main)] border-t border-[var(--gold-main)]/25 text-[var(--text-muted)] pt-16 pb-8 mt-24 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-[var(--gold-main)]/5 blur-[150px] pointer-events-none rounded-full" />

      <div className="max-w-[1800px] mx-auto px-4 sm:px-8 lg:px-12 relative z-10 space-y-12">
        {/* Value Proposition Grid */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-6 pb-12 border-b border-[var(--border)] ${
            valueProps.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
          }`}
        >
          {valueProps.map(({ icon: Icon, title, subtext }) => (
            <div
              key={title}
              className="flex items-center gap-3.5 p-3 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border)]"
            >
              <div className="w-12 h-12 rounded-full bg-[var(--gold-main)]/15 border border-[var(--gold-main)]/30 flex items-center justify-center text-[var(--gold-main)] shrink-0">
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-[var(--text-primary)] font-title">{title}</h4>
                <p className="text-[11px] text-[var(--text-faint)]">{subtext}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Links & Brand Info */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 py-4">
          <div className="space-y-4">
            <div className="flex items-center gap-2.5">
              {config.logo_url ? (
                <img src={assetSrc(config.logo_url)} alt={config.brand_name} className="h-9 w-auto object-contain" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-[var(--gold-deep)] via-[var(--gold-main)] to-[var(--gold-light)] p-0.5 shadow-md">
                  <div className="w-full h-full rounded-full bg-[var(--bg-main)] flex items-center justify-center text-[var(--gold-main)] font-title font-bold text-base">
                    {config.brand_name ? config.brand_name.charAt(0) : 'B'}
                  </div>
                </div>
              )}
              <span className="text-lg font-title font-bold gold-shimmer-text uppercase">{config.brand_name}</span>
            </div>
            <p className="text-xs leading-relaxed text-[var(--text-muted)] font-light">
              {config.footer_brand_blurb ||
                `${config.brand_name} — ${config.brand_tagline}. Handcrafted gold plated covering jewellery — Kundan rings, temple harams, CZ bracelets, jhumkas and fashion accessories.`}
            </p>
            {gstOn && (
              <div className="inline-block px-3 py-1 bg-[var(--gold-main)]/10 border border-[var(--gold-main)]/20 rounded-md text-[10px] text-[var(--gold-main)] font-mono font-semibold">
                GSTIN: {config.gstin_number}
              </div>
            )}
          </div>

          <div>
            <h5 className="text-xs font-title font-bold text-[var(--gold-main)] uppercase tracking-wider mb-4">
              {config.footer_collections_title || 'Curated Collections'}
            </h5>
            <ul className="space-y-2.5 text-xs text-[var(--text-muted)] font-medium">
              {footerCategories.map((cat) => (
                <li key={cat.slug}>
                  <a href="#catalog" className="hover:text-[var(--gold-main)] transition flex items-center gap-1">
                    &rsaquo; {cat.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h5 className="text-xs font-title font-bold text-[var(--gold-main)] uppercase tracking-wider mb-4">
              {config.footer_care_title || 'Customer Care & Help'}
            </h5>
            <ul className="space-y-2.5 text-xs text-[var(--text-muted)] font-medium">
              {careLinks.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="hover:text-[var(--gold-main)] transition flex items-center gap-1">
                    &rsaquo; {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h5 className="text-xs font-title font-bold text-[var(--text-primary)] uppercase tracking-wider mb-4 text-[var(--gold-main)]">
              Store Showroom & Contact
            </h5>
            <ul className="space-y-3 text-xs text-[var(--text-primary)]">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-[var(--gold-main)] shrink-0 mt-0.5" />
                <span>{config.store_address}</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-[var(--gold-main)] shrink-0" />
                <a href={`tel:${config.contact_phone}`} className="hover:text-[var(--gold-main)] transition">{config.contact_phone}</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-[var(--gold-main)] shrink-0" />
                <a href={`mailto:${config.contact_email}`} className="hover:text-[var(--gold-main)] transition">{config.contact_email}</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright & Yonraa Tech Branding */}
        <div className="pt-6 border-t border-[var(--border)] flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[var(--text-faint)]">
          <p>© {new Date().getFullYear()} {companyLegalName(config)}. All rights reserved.</p>
          <a
            href="https://yonraa.pages.dev"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs py-1 px-3 rounded-full bg-[var(--bg-surface-2)] border border-[var(--border)] hover:border-[var(--gold-main)] transition cursor-pointer"
            title="Visit Yonraa Tech"
          >
            <span className="text-[var(--text-muted)] text-[11px]">Powered by</span>
            <img src="/yonraa-tech-logo.png" alt="Yonraa Tech" className="h-5 w-auto object-contain" />
            <span className="font-bold text-[var(--text-primary)] text-[11px] tracking-wide hover:text-[var(--gold-main)]">Yonraa Tech</span>
          </a>
        </div>
      </div>
    </footer>
  )
}
